/**
 * Prepaid wallet — tenant API (backend: modules/wallet, /admin/billing/wallet/*).
 * Amounts are rupees (numbers, up to 4 decimals) exactly as the API returns them.
 */

import api from './api.service';
import type { RequestOptions } from './admin.api';

export type WalletAiMode = 'off' | 'wallet' | 'quota_then_wallet';
export type WalletMetaMode = 'off' | 'platform_fee' | 'pass_through';

export interface WalletSummary {
  enabled: boolean;
  status: 'active' | 'frozen';
  meters: { ai: WalletAiMode; whatsapp: WalletMetaMode };
  currency: string;
  balance: number;
  paid: number;
  promo: number;
  /** Balance minus charges in flight — what can actually be spent right now. */
  available: number;
  inFlight: number;
  creditLimit: number;
  /** Kept aside for customer conversations (AI, flows, API) — campaigns can't spend it. */
  conversationReserve?: number;
  lowBalanceThreshold: number;
  lowBalance: boolean;
  topup: { allowSelfServe: boolean; min: number; max: number };
  lastActivityAt?: string;
  /** Hourly runway estimate from the last 7 days of usage. */
  forecast?: { burnPerDay: number; daysLeft?: number; at: string };
}

export type WalletJournalKind =
  | 'topup'
  | 'usage_ai'
  | 'usage_meta'
  | 'adjustment_credit'
  | 'adjustment_debit'
  | 'promo_grant'
  | 'refund';

export interface WalletJournalRow {
  id: string;
  seq: number;
  kind: WalletJournalKind;
  meter?: 'ai' | 'meta';
  amount: number;
  balanceAfter: number;
  paidAfter: number;
  promoAfter: number;
  occurredAt: string;
  createdAt: string;
  reason?: string;
  category?: string;
  country?: string;
  model?: string;
  wamid?: string;
  paymentTransactionId?: string;
}

export interface WalletJournalPage {
  rows: WalletJournalRow[];
  nextBeforeSeq?: number;
}

/**
 * One row of the grouped statement (backend wallet-statement.service.ts):
 * a campaign run, a day of one WhatsApp message type, a day of AI, or a single
 * top-up/adjustment. Expanding it lists the charges behind it (getStatementEntries).
 */
export interface WalletStatementLine {
  id: string;
  group: 'campaign' | 'day' | 'single';
  kind: WalletJournalKind;
  meter?: 'ai' | 'meta';
  /** IST day, YYYY-MM-DD — `day` rows only. */
  day?: string;
  campaignRunId?: string;
  campaignId?: string;
  campaignName?: string;
  category?: string;
  country?: string;
  /** Charges folded into this row. */
  count: number;
  amount: number;
  balanceAfter: number;
  firstAt: string;
  lastAt: string;
  reason?: string;
  model?: string;
}

export interface WalletStatementPage {
  rows: WalletStatementLine[];
  nextBeforeSeq?: number;
}

export interface CreateTopupOrderResponse {
  transactionId: string;
  gatewayOrderId: string;
  paymentSessionId?: string;
  amount: number;
  currency: string;
  walletCredit: number;
  gateway: 'cashfree';
}

/** Server-derived — see backend modules/wallet/wallet-topup-status.ts. */
export type TopupState = 'credited' | 'crediting' | 'processing' | 'awaiting' | 'cancelled' | 'failed' | 'expired';

export interface TopupStatus {
  gatewayOrderId: string;
  transactionId: string;
  state: TopupState;
  amount: number;
  currency: string;
  walletCredit: number;
  createdAt?: string;
  paidAt?: string;
  reason?: string;
  method?: string;
  canRetry: boolean;
  paymentSessionId?: string;
  balance?: number;
}

export interface TopupListRow {
  gatewayOrderId: string;
  amount: number;
  walletCredit: number;
  state: TopupState;
  createdAt?: string;
  paidAt?: string;
}

const BASE = '/admin/billing/wallet';

const walletApi = {
  async getSummary(opts?: RequestOptions): Promise<WalletSummary> {
    const res = await api.get<{ success: boolean; data: WalletSummary }>(BASE, opts);
    return res.data;
  },

  async getJournal(
    params: { beforeSeq?: number | undefined; limit?: number | undefined; kind?: WalletJournalKind[] | undefined } = {}
  ): Promise<WalletJournalPage> {
    const res = await api.get<{ success: boolean; data: WalletJournalPage }>(`${BASE}/journal`, {
      params: {
        ...(params.beforeSeq ? { beforeSeq: params.beforeSeq } : {}),
        limit: params.limit ?? 50,
        ...(params.kind?.length ? { kind: params.kind.join(',') } : {}),
      },
    });
    return res.data;
  },

  async getStatement(
    params: { beforeSeq?: number | undefined; limit?: number | undefined; kind?: WalletJournalKind[] | undefined } = {}
  ): Promise<WalletStatementPage> {
    const res = await api.get<{ success: boolean; data: WalletStatementPage }>(`${BASE}/statement`, {
      params: {
        ...(params.beforeSeq ? { beforeSeq: params.beforeSeq } : {}),
        limit: params.limit ?? 25,
        ...(params.kind?.length ? { kind: params.kind.join(',') } : {}),
      },
    });
    return res.data;
  },

  /** The individual charges behind one statement row, newest first. */
  async getStatementEntries(
    lineId: string,
    params: { beforeSeq?: number | undefined; limit?: number | undefined } = {}
  ): Promise<WalletJournalPage> {
    const res = await api.get<{ success: boolean; data: WalletJournalPage }>(
      `${BASE}/statement/${encodeURIComponent(lineId)}/entries`,
      { params: { ...(params.beforeSeq ? { beforeSeq: params.beforeSeq } : {}), limit: params.limit ?? 50 } }
    );
    return res.data;
  },

  async createTopupOrder(payload: { amount: number; phoneNumber?: string }): Promise<CreateTopupOrderResponse> {
    const res = await api.post<{ success: boolean; data: CreateTopupOrderResponse }>(`${BASE}/topup/create-order`, payload);
    return res.data;
  },

  /** Idempotent — safe to poll. Advances the order server-side when the gateway says it's paid. */
  async confirmTopup(gatewayOrderId: string): Promise<TopupStatus> {
    const res = await api.post<{ success: boolean; data: TopupStatus }>(`${BASE}/topup/confirm`, { gatewayOrderId });
    return res.data;
  },

  async listTopups(limit = 10): Promise<TopupListRow[]> {
    const res = await api.get<{ success: boolean; data: { rows: TopupListRow[] } }>(`${BASE}/topup/orders`, {
      params: { limit },
    });
    return res.data.rows;
  },
};

export default walletApi;

/** The backend's human message for an axios error, else a fallback. */
export function walletErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error?.message || (err?.code === 'ERR_NETWORK' ? 'Network error — check your connection and try again.' : '') || fallback;
}

export const formatInr = (n: number | undefined | null, opts: { decimals?: number } = {}): string => {
  const v = Number(n ?? 0);
  const decimals = opts.decimals ?? 2;
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};
